import {
    ChallengeAggregationType,
    ChallengeDisplayNormalizationFormulaDto,
    ChallengeFitnessMetricType,
    ChallengeNormalizationOperation,
    ChallengeNormalizationOutputFormat,
    ChallengeRuleType
} from '@api/ChallengeClient';

export interface ChallengeResultFormat {
    fitnessMetricType?: ChallengeFitnessMetricType;
    ruleType?: ChallengeRuleType;
    aggregationType?: ChallengeAggregationType;
    displayNormalizationFormula?: ChallengeDisplayNormalizationFormulaDto;
}

function formatDurationSeconds(totalSeconds: number): string {
    const rounded = Math.round(Math.abs(totalSeconds));
    const hours = Math.floor(rounded / 3600);
    const minutes = Math.floor((rounded % 3600) / 60);
    const seconds = rounded % 60;
    const sign = totalSeconds < 0 ? '-' : '';

    const paddedSeconds = String(seconds).padStart(2, '0');

    if (hours > 0) {
        return `${sign}${hours}:${String(minutes).padStart(
            2,
            '0'
        )}:${paddedSeconds}`;
    }

    return `${sign}${minutes}:${paddedSeconds}`;
}

const metricUnitLabels: Record<ChallengeFitnessMetricType, string> = {
    [ChallengeFitnessMetricType.PACE]: 'Seconds',
    [ChallengeFitnessMetricType.DISTANCE]: 'Metres',
    [ChallengeFitnessMetricType.ACTIVITY_TIME_IN_MINUTES]: 'Minutes',
    [ChallengeFitnessMetricType.STEPS]: 'Steps',
    [ChallengeFitnessMetricType.CALORIES]: 'Calories',
    [ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC]: 'Seconds',
    [ChallengeFitnessMetricType.REPS]: 'Reps'
};

const metricNameLabels: Record<ChallengeFitnessMetricType, string> = {
    [ChallengeFitnessMetricType.PACE]: 'Pace',
    [ChallengeFitnessMetricType.DISTANCE]: 'Distance',
    [ChallengeFitnessMetricType.ACTIVITY_TIME_IN_MINUTES]: 'Time',
    [ChallengeFitnessMetricType.STEPS]: 'Steps',
    [ChallengeFitnessMetricType.CALORIES]: 'Calories',
    [ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC]: 'Calculated metric',
    [ChallengeFitnessMetricType.REPS]: 'Reps'
};

const secondsValuedMetrics = [
    ChallengeFitnessMetricType.PACE,
    ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC
];

function isSecondsValued(format: ChallengeResultFormat): boolean {
    return (
        format.fitnessMetricType !== undefined &&
        secondsValuedMetrics.includes(format.fitnessMetricType)
    );
}

export function normalizationFormulaOf(
    format: ChallengeResultFormat
): ChallengeDisplayNormalizationFormulaDto | undefined {
    const formula = format.displayNormalizationFormula;

    if (
        !formula ||
        format.ruleType !== ChallengeRuleType.ACTIVITY_RAW_VALUE ||
        formula.operation !== ChallengeNormalizationOperation.MULTIPLY ||
        !(formula.operand > 0)
    ) {
        return undefined;
    }

    return formula;
}

function applyNormalization(
    value: number,
    format: ChallengeResultFormat
): number {
    const formula = normalizationFormulaOf(format);

    return formula ? value * formula.operand : value;
}

export function formatChallengeResult(
    result: number | undefined,
    format: ChallengeResultFormat
): string {
    if (result === undefined || result === null) {
        return '–';
    }

    if (format.ruleType === ChallengeRuleType.ACTIVITY_COUNT) {
        return String(result);
    }

    const formula = normalizationFormulaOf(format);

    if (formula) {
        if (result === 0) {
            return '–';
        }

        const normalized = applyNormalization(result, format);

        return formula.outputFormat ===
            ChallengeNormalizationOutputFormat.DURATION
            ? formatDurationSeconds(normalized)
            : String(normalized);
    }

    if (isSecondsValued(format)) {
        return result === 0 ? '–' : formatDurationSeconds(result);
    }

    return String(result);
}

export function storedValueUnitLabel(format: ChallengeResultFormat): string {
    if (format.ruleType === ChallengeRuleType.ACTIVITY_COUNT) {
        return 'Activities';
    }

    return format.fitnessMetricType === undefined
        ? 'Activity points'
        : metricUnitLabels[format.fitnessMetricType];
}

export function storedValueDiffersFromDisplay(
    format: ChallengeResultFormat
): boolean {
    return (
        normalizationFormulaOf(format) !== undefined || isSecondsValued(format)
    );
}

export function challengeResultMetricLabel(
    format: ChallengeResultFormat
): string | undefined {
    if (
        format.ruleType === undefined ||
        format.ruleType === ChallengeRuleType.ACTIVITY_POINTS
    ) {
        return undefined;
    }

    if (format.ruleType === ChallengeRuleType.ACTIVITY_COUNT) {
        return 'Activities';
    }

    if (format.fitnessMetricType === undefined) {
        return undefined;
    }

    if (
        isSecondsValued(format) &&
        normalizationFormulaOf(format)?.outputFormat ===
            ChallengeNormalizationOutputFormat.DURATION
    ) {
        return 'Time';
    }

    return metricNameLabels[format.fitnessMetricType];
}

export function challengeRankingLabel(
    format: ChallengeResultFormat
): string | undefined {
    const metric = challengeResultMetricLabel(format);

    if (metric === undefined || format.aggregationType === undefined) {
        return undefined;
    }

    const direction =
        format.aggregationType === ChallengeAggregationType.MIN
            ? 'lowest wins'
            : 'highest wins';

    return `${metric} • ${direction}`;
}
